import type { LucideIcon } from "lucide-react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

type DeltaTone = "good" | "bad" | "neutral";

export interface KpiCardProps {
  label: string;
  value: string;
  hint?: string;
  icon?: LucideIcon;
  delta?: {
    value: string;
    tone: DeltaTone;
  } | null;
  loading?: boolean;
}

export function KpiCard({
  label,
  value,
  hint,
  icon: Icon,
  delta,
  loading,
}: KpiCardProps) {
  return (
    <Card className="overflow-hidden bg-gradient-to-br from-card to-background/40">
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-start justify-between text-muted-foreground">
          <div className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wider">
              {label}
            </span>
          </div>
          {Icon ? (
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary/40 text-foreground/70">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
        </div>
        {loading ? (
          <Skeleton className="h-9 w-32" />
        ) : (
          <div className="text-3xl font-semibold tracking-tight tabular-nums">
            {value}
          </div>
        )}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          {delta ? <DeltaPill {...delta} /> : null}
          {hint ? <span className="truncate">{hint}</span> : null}
        </div>
      </CardContent>
    </Card>
  );
}

function DeltaPill({ value, tone }: { value: string; tone: DeltaTone }) {
  const Icon =
    tone === "good"
      ? ArrowDownRight
      : tone === "bad"
        ? ArrowUpRight
        : Minus;
  const colorClass =
    tone === "good"
      ? "text-[color:var(--color-success)] bg-[color:var(--color-success)]/10"
      : tone === "bad"
        ? "text-destructive bg-destructive/10"
        : "text-muted-foreground bg-muted";
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium tabular-nums",
        colorClass
      )}
    >
      <Icon className="h-3 w-3" />
      {value}
    </span>
  );
}
